/**
 * Scaling Up Cockpit — main dashboard ("helicopter view") for the founder /
 * board. Single-glance state of OPSP, critical number, priorities, KPI
 * scoreboard, upcoming meetings, overdue action items, open issues.
 *
 * Access: super_admin (today). Holding owner / board member roles will be
 * supported via membership lookup in a follow-up.
 */
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Building2,
  Target,
  AlertOctagon,
  ListChecks,
  CalendarClock,
  Compass,
  Sparkles,
  Users,
  MessageSquare,
  ArrowLeft,
  BookOpen,
} from "lucide-react";

interface Holding {
  id: string;
  name: string;
  slug: string;
}
interface Company {
  id: string;
  holdingId: string;
  name: string;
  slug: string;
}

interface DashboardSummary {
  scope: { holdingId: string; companyId: string | null };
  vision: { bhag: string | null; corePurpose: string | null; revisedAt: string | null } | null;
  criticalNumber: { kpiId: string; name: string; status: "red" | "yellow" | "green" } | null;
  priorities: ReadonlyArray<{
    id: string;
    title: string;
    status: string;
    ownerUserId: string | null;
  }>;
  kpiScoreboard: ReadonlyArray<{
    id: string;
    name: string;
    currentValue: number | null;
    target: number | null;
    status: "red" | "yellow" | "green" | null;
    lastUpdatedAt: string | null;
  }>;
  upcomingMeetings: ReadonlyArray<{ id: string; type: string; scheduledAt: string | null }>;
  overdueActionItems: ReadonlyArray<{
    id: string;
    description: string;
    ownerUserId: string | null;
    dueDate: string | null;
  }>;
  openIssues: ReadonlyArray<{ id: string; title: string; severity: string }>;
}

const STATUS_COLOR: Record<string, string> = {
  red: "bg-red-500/15 text-red-300 border-red-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  on_track: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  at_risk: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  off_track: "bg-red-500/15 text-red-300 border-red-500/30",
  done: "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

function statusClass(status: string | null | undefined): string {
  if (!status) return "bg-white/5 text-white/60 border-white/10";
  return STATUS_COLOR[status] ?? "bg-white/5 text-white/60 border-white/10";
}

function ScalingUpCockpitInner() {
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const holdingsQuery = useQuery<{ items: Holding[] }>({
    queryKey: ["/api/scaling-up/holdings"],
  });

  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", "companies"],
    enabled: holdingId !== null,
  });

  const dashboardQuery = useQuery<DashboardSummary>({
    queryKey: [
      "/api/scaling-up/holdings",
      holdingId ?? "",
      "companies",
      companyId ?? "",
      "dashboard",
    ],
    enabled: holdingId !== null && companyId !== null,
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const dashboard = dashboardQuery.data;

  const visionLine = useMemo(() => {
    if (!dashboard?.vision) return null;
    const parts: string[] = [];
    if (dashboard.vision.bhag) parts.push(`BHAG: ${dashboard.vision.bhag}`);
    if (dashboard.vision.corePurpose) parts.push(dashboard.vision.corePurpose);
    return parts.join(" • ");
  }, [dashboard]);

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <div className="mb-4">
        <Link href="/admin">
          <Button variant="ghost" size="sm" className="text-white/60 hover:text-white -ml-3">
            <ArrowLeft className="w-4 h-4 mr-1" /> Wróć do panelu admina
          </Button>
        </Link>
      </div>
      <header className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-1">
            Scaling Up Cockpit
          </div>
          <h1 className="text-2xl lg:text-3xl font-extrabold">Helicopter View</h1>
          {visionLine && <p className="text-white/60 text-sm mt-2 max-w-2xl">{visionLine}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link href="/admin/scaling-up/vision">
            <Button variant="outline" size="sm">
              <Compass className="w-4 h-4 mr-2" /> Vision
            </Button>
          </Link>
          <Link href="/admin/scaling-up/kpis">
            <Button variant="outline" size="sm">
              <Target className="w-4 h-4 mr-2" /> KPIs
            </Button>
          </Link>
          <Link href="/admin/scaling-up/meetings">
            <Button variant="outline" size="sm">
              <CalendarClock className="w-4 h-4 mr-2" /> Meetings
            </Button>
          </Link>
          <Link href="/admin/scaling-up/action-items">
            <Button variant="outline" size="sm">
              <ListChecks className="w-4 h-4 mr-2" /> Actions
            </Button>
          </Link>
          <Link href="/admin/scaling-up/issues">
            <Button variant="outline" size="sm">
              <AlertOctagon className="w-4 h-4 mr-2" /> Issues
            </Button>
          </Link>
          <Link href="/admin/scaling-up/people">
            <Button variant="outline" size="sm">
              <Users className="w-4 h-4 mr-2" /> People
            </Button>
          </Link>
          <Link href="/admin/scaling-up/holdings">
            <Button variant="outline" size="sm">
              <Building2 className="w-4 h-4 mr-2" /> Holdings
            </Button>
          </Link>
        </div>
      </header>

      {/* Scope picker */}
      <Card className="mb-6 border-white/10 bg-white/[0.02]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <Building2 className="w-4 h-4 text-white/60" />
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={holdingId ?? ""}
            onChange={(e) => {
              setHoldingId(e.target.value || null);
              setCompanyId(null);
            }}
          >
            <option value="">Wybierz holding…</option>
            {holdings.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm disabled:opacity-50"
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(e.target.value || null)}
            disabled={!holdingId}
          >
            <option value="">Wybierz spółkę…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {holdings.length === 0 && !holdingsQuery.isLoading && (
        <Card className="mb-6 border-emerald-500/25 bg-emerald-500/[0.04]">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-emerald-100 font-semibold mb-1">
                  Pierwsze kroki w Scaling Up Cockpicie
                </div>
                <ol className="text-white/70 text-sm space-y-1 list-decimal list-inside mb-3">
                  <li>
                    Utwórz <strong>holding</strong> (np. „Viral Partners Holding") — to nadrzędny
                    tenant
                  </li>
                  <li>
                    Dodaj <strong>spółki</strong> w ramach holdingu — każda ma własne KPIs / Vision
                    / Issues
                  </li>
                  <li>
                    Przypisz <strong>członków</strong> (board / executives / team) z istniejących
                    userów
                  </li>
                  <li>Wróć tu — wybierz holding i spółkę żeby zobaczyć Helicopter View</li>
                </ol>
                <Link href="/admin/scaling-up/holdings">
                  <Button
                    size="sm"
                    className="bg-emerald-500/80 hover:bg-emerald-500 text-emerald-950"
                  >
                    <Building2 className="w-4 h-4 mr-2" /> Utwórz pierwszy holding
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {holdings.length > 0 && !companyId && (
        <div className="text-white/40 text-sm">
          Wybierz holding i spółkę powyżej, aby zobaczyć dashboard.{" "}
          <Link href="/admin/scaling-up/holdings">
            <span className="underline hover:text-white/70 cursor-pointer">
              Zarządzaj holdingami →
            </span>
          </Link>
        </div>
      )}

      {dashboardQuery.isLoading && <CockpitSkeleton />}

      {dashboard && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Critical Number */}
          <Card className="border-white/10 bg-white/[0.02] lg:col-span-1">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider mb-3">
                <Sparkles className="w-4 h-4" /> Critical Number (Kwartał)
              </div>
              {dashboard.criticalNumber ? (
                <>
                  <div className="text-lg font-bold mb-1">{dashboard.criticalNumber.name}</div>
                  <Badge className={`border ${statusClass(dashboard.criticalNumber.status)}`}>
                    {dashboard.criticalNumber.status.toUpperCase()}
                  </Badge>
                </>
              ) : (
                <div className="text-white/40 text-sm">Brak Critical Number na ten kwartał.</div>
              )}
            </CardContent>
          </Card>

          {/* Priorities */}
          <Card className="border-white/10 bg-white/[0.02] lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider mb-3">
                <Target className="w-4 h-4" /> Priorities (Rocks)
              </div>
              {dashboard.priorities.length === 0 ? (
                <div className="text-white/40 text-sm">Brak priorytetów.</div>
              ) : (
                <ul className="space-y-2">
                  {dashboard.priorities.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate">{p.title}</span>
                      <Badge className={`border whitespace-nowrap ${statusClass(p.status)}`}>
                        {p.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* KPI Scoreboard */}
          <Card className="border-white/10 bg-white/[0.02] lg:col-span-3">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider mb-3">
                <Target className="w-4 h-4" /> KPI Scoreboard
              </div>
              {dashboard.kpiScoreboard.length === 0 ? (
                <div className="text-white/40 text-sm">Brak KPI zdefiniowanych.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {dashboard.kpiScoreboard.map((k) => (
                    <div key={k.id} className="border border-white/10 rounded p-3 bg-black/20">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="text-sm font-semibold truncate">{k.name}</div>
                        {k.status && (
                          <Badge className={`border text-[10px] ${statusClass(k.status)}`}>
                            {k.status.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-white/60">
                        {k.currentValue ?? "—"}
                        {k.target !== null ? ` / target ${k.target}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Meetings */}
          <Card className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider mb-3">
                <CalendarClock className="w-4 h-4" /> Nadchodzące spotkania
              </div>
              {dashboard.upcomingMeetings.length === 0 ? (
                <div className="text-white/40 text-sm">Brak zaplanowanych.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {dashboard.upcomingMeetings.map((m) => (
                    <li key={m.id} className="flex justify-between gap-2">
                      <span className="capitalize">{m.type}</span>
                      <span className="text-white/50 text-xs">
                        {m.scheduledAt ? new Date(m.scheduledAt).toLocaleString() : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Overdue action items */}
          <Card className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider mb-3">
                <ListChecks className="w-4 h-4" /> Zaległe action items
              </div>
              {dashboard.overdueActionItems.length === 0 ? (
                <div className="text-white/40 text-sm">Brak zaległych. 🎯</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {dashboard.overdueActionItems.map((a) => (
                    <li key={a.id} className="flex justify-between gap-2">
                      <span className="truncate">{a.description}</span>
                      <span className="text-red-300/80 text-xs whitespace-nowrap">
                        {a.dueDate ?? "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Open Issues */}
          <Card className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider mb-3">
                <AlertOctagon className="w-4 h-4" /> Open Issues
              </div>
              {dashboard.openIssues.length === 0 ? (
                <div className="text-white/40 text-sm">Brak otwartych.</div>
              ) : (
                <ul className="space-y-2 text-sm">
                  {dashboard.openIssues.map((i) => (
                    <li key={i.id} className="flex justify-between gap-2">
                      <span className="truncate">{i.title}</span>
                      <span className="text-xs text-white/50 capitalize">{i.severity}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

/** Skeleton pierwszego ładowania — odwzorowuje siatkę dashboardu (Critical Number, Priorities, KPI Scoreboard, 3 listy), żeby po załadowaniu nie było skoku layoutu. */
function CockpitSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="border-white/10 bg-white/[0.02] lg:col-span-1">
        <CardContent className="p-5">
          <Skeleton className="h-4 w-44 mb-3" />
          <Skeleton className="h-6 w-32 mb-2" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </CardContent>
      </Card>
      <Card className="border-white/10 bg-white/[0.02] lg:col-span-2">
        <CardContent className="p-5">
          <Skeleton className="h-4 w-36 mb-3" />
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="border-white/10 bg-white/[0.02] lg:col-span-3">
        <CardContent className="p-5">
          <Skeleton className="h-4 w-32 mb-3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border border-white/10 rounded p-3 bg-black/20">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-4 w-12 rounded-full" />
                </div>
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className="border-white/10 bg-white/[0.02]">
          <CardContent className="p-5">
            <Skeleton className="h-4 w-40 mb-3" />
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j} className="flex items-center justify-between gap-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function ScalingUpCockpitPage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpCockpitInner />
    </AdminGuard>
  );
}
